'use client';

import type { ReactNode } from 'react';
import { Children, Fragment, isValidElement } from 'react';
import ReactMarkdown from 'react-markdown';

/**
 * V2.10 — render LLM responses as styled markdown rather than raw text.
 * Gemini emits `### headers`, `**bold**`, `*` bullet lists, etc. We keep
 * the renderer scoped to a known set of inline + block constructs so the
 * output blends into our cream/navy palette and doesn't fight the chat
 * bubble's padding. No images / no html — chat is text-only here.
 *
 * V2.10h: parse the two ABSOLUTE-RULE markers the system prompt requires
 * the model to emit, and render them visually instead of as raw text:
 *
 *   - `[实时]` / `[历史]` / `[基于推断]` confidence tags inline → colored
 *     pill so a client can scan paragraph headers and immediately see
 *     "this conclusion is fresh tool data" vs "this is the economist's
 *     historical position" vs "this is inference, treat skeptically".
 *
 *   - `> 数据来源: ...` / `> Source: ...` / `> 引用: ...` blockquotes →
 *     a labeled callout with an icon and tighter type so the source line
 *     reads like attribution metadata, not commentary.
 */
type ConfKind = 'realtime' | 'historical' | 'inferred';

// 2026-06-04 — `title` tooltips removed. Previous wording mentioned
// "vault" and "production 配置" verbatim; even though those strings
// only rendered on hover, CRO-63 #3 showed the LLM was echoing the
// same phrasing it saw in the system prompt. We've cleaned the system
// prompt; not exposing the same internal-vocabulary tooltip avoids a
// cosmetic surface where the leak could re-surface.
const CONF_LABELS: Record<ConfKind, { zh: string; tone: string; title: string }> = {
  realtime: {
    zh: '实时',
    tone: 'bg-[#2B5BD7]/10 text-[#2B5BD7] border-[#2B5BD7]/30',
    title: '实时数据（来自工具调用）',
  },
  historical: {
    zh: '历史',
    tone: 'bg-gold-pale text-gold-dark border-gold-light',
    title: '已发表的历史立场（含日期）',
  },
  inferred: {
    zh: '基于推断',
    tone: 'bg-cream text-warmth border-warmth/40',
    title: '基于框架的现场判断',
  },
};

/**
 * 2026-06-04 (CRO-63 #3 sanitizer) — defense-in-depth strip of internal
 * prompt vocabulary that should never reach the bubble. The real fix is
 * in containers/hermes/app/system_prompts.py (the prompt no longer
 * contains "vault" / "production 配置下应降级" / "基于训练数据"). But if
 * an older container is still running, or if a future prompt edit
 * accidentally leaks the same vocabulary, this sanitizer keeps the UI
 * clean. Side-effect-free; idempotent; no-op on clean content.
 *
 * Strategy: scrub the specific phrases that have been observed leaking
 * (or are likely to leak) — not a broad keyword block, because legitimate
 * answers may discuss "vault" if a real institutional client asks about
 * data infrastructure.
 */
const DEBUG_LEAK_PATTERNS: RegExp[] = [
  // The full sentence from the old prompt's ABSOLUTE-RULE block.
  /机构客户\s*production\s*配置下[^。\n]*?(?:必须降级|应降级)[^。\n]*?。/g,
  // Partial phrasing the LLM tends to paraphrase from the same sentence.
  /基于训练数据\s*\/?\s*推理[^。\n]*?(?:不是\s*vault\s*内容|非\s*vault\s*内容)[^。\n]*?。/g,
  // Standalone "应降级 …" hint left over after the leading clause was rewritten.
  /\(?机构\s*production\s*[^)\n]{0,20}\)?\s*应降级[^。\n]*?。/g,
];

export function sanitizeLlmOutput(raw: string): string {
  let out = raw;
  for (const pat of DEBUG_LEAK_PATTERNS) {
    out = out.replace(pat, '');
  }
  // Collapse double newlines opened by removed sentences so the
  // paragraph spacing doesn't go haywire after a strip.
  return out.replace(/\n{3,}/g, '\n\n');
}

function ConfidencePill({ kind }: { kind: ConfKind }) {
  const s = CONF_LABELS[kind];
  return (
    <span
      title={s.title}
      className={`inline-flex items-center px-1.5 py-0.5 mx-[2px] text-[10px] lg:text-[11px] font-medium rounded border align-middle ${s.tone}`}
    >
      [{s.zh}]
    </span>
  );
}

const CONF_PATTERN = /(\[实时\]|\[历史\]|\[基于推断\])/g;

/** Replace bare `[实时]` / `[历史]` / `[基于推断]` tokens inside any text
 *  child with a styled pill. Recursively walks the child tree so the
 *  pills survive nesting inside <strong>, <em>, <li>, etc. */
function replaceConfTags(children: ReactNode): ReactNode {
  if (typeof children === 'string') {
    if (!CONF_PATTERN.test(children)) {
      CONF_PATTERN.lastIndex = 0;
      return children;
    }
    CONF_PATTERN.lastIndex = 0;
    const parts = children.split(CONF_PATTERN);
    return parts.map((part, i) => {
      // Append the slot index to a fingerprint of the surrounding text so
      // the key is stable even if the same tag appears multiple times in
      // one paragraph. We use part + i because part alone collides on
      // duplicate text segments (e.g. two "[历史]" in a row).
      const k = `${part}-${i}`;
      if (part === '[实时]') return <ConfidencePill key={k} kind="realtime" />;
      if (part === '[历史]') return <ConfidencePill key={k} kind="historical" />;
      if (part === '[基于推断]') return <ConfidencePill key={k} kind="inferred" />;
      return <Fragment key={k}>{part}</Fragment>;
    });
  }
  if (Array.isArray(children)) {
    return children.map((c, i) => (
      <Fragment key={`f-${i}-${typeof c === 'string' ? c.slice(0, 8) : ''}`}>
        {replaceConfTags(c)}
      </Fragment>
    ));
  }
  return children;
}

/** Inspect a blockquote's children to see if it leads with `数据来源:` /
 *  `Source:` / `引用:`. Returns the matched label + the trailing content
 *  so the caller can render a labeled callout. Null = not a source quote. */
function parseSourceBlock(
  children: ReactNode,
): { label: 'source' | 'citation'; tone: string; rest: ReactNode } | null {
  // Walk into the first <p>'s first text child.
  const arr = Children.toArray(children);
  for (const node of arr) {
    if (!isValidElement(node)) continue;
    // typed-as-any here so we can poke at children regardless of element type
    const props = node.props as { children?: ReactNode };
    const inner = Children.toArray(props.children);
    const first = inner[0];
    if (typeof first !== 'string') continue;
    const match = /^\s*(数据来源|Source|引用|Citation)\s*[:：]\s*(.*)$/s.exec(first);
    if (!match) continue;
    const labelKind = match[1] === '数据来源' || match[1] === 'Source' ? 'source' : 'citation';
    const restText = match[2];
    const restChildren: ReactNode[] = [restText, ...inner.slice(1)];
    return {
      label: labelKind,
      tone:
        labelKind === 'source'
          ? 'bg-sage/10 border-sage/30 text-sage-dark'
          : 'bg-cream/60 border-soft text-dusk',
      rest: <p className="my-0">{restChildren}</p>,
    };
  }
  return null;
}

export function MarkdownContent({
  content,
  className,
}: {
  content: string;
  /** Override the base text size / color. Defaults bump up on lg so PC users
   *  reading dense economist content don't have to squint. */
  className?: string;
}) {
  const cls = className ?? 'text-[15px] lg:text-base leading-relaxed';
  const safe = sanitizeLlmOutput(content);
  return (
    <div className={`markdown-body ${cls}`}>
      <ReactMarkdown
        // Disable raw HTML by NOT enabling rehype-raw. allowedElements
        // controls render whitelist; pass undefined to allow all builtin
        // markdown ones (we override their classes below).
        components={{
          h1: ({ children }) => (
            <h1 className="font-serif text-lg text-navy mt-3 mb-1.5 first:mt-0">
              {replaceConfTags(children)}
            </h1>
          ),
          h2: ({ children }) => (
            <h2 className="font-serif text-base text-navy mt-3 mb-1.5 first:mt-0">
              {replaceConfTags(children)}
            </h2>
          ),
          h3: ({ children }) => (
            <h3 className="font-serif text-[15px] text-navy font-semibold mt-2.5 mb-1 first:mt-0">
              {replaceConfTags(children)}
            </h3>
          ),
          h4: ({ children }) => (
            <h4 className="font-serif text-[14px] text-navy font-semibold mt-2 mb-1 first:mt-0">
              {replaceConfTags(children)}
            </h4>
          ),
          p: ({ children }) => (
            <p className="my-1.5 first:mt-0 last:mb-0">{replaceConfTags(children)}</p>
          ),
          ul: ({ children }) => (
            <ul className="list-disc list-outside pl-5 my-1.5 space-y-0.5">{children}</ul>
          ),
          ol: ({ children }) => (
            <ol className="list-decimal list-outside pl-5 my-1.5 space-y-0.5">{children}</ol>
          ),
          li: ({ children }) => <li className="leading-relaxed">{replaceConfTags(children)}</li>,
          strong: ({ children }) => (
            <strong className="font-semibold text-navy">{replaceConfTags(children)}</strong>
          ),
          em: ({ children }) => <em className="italic">{replaceConfTags(children)}</em>,
          code: ({ children }) => (
            <code className="font-mono text-[13px] px-1 py-0.5 rounded bg-cream/80 border border-soft text-ink">
              {children}
            </code>
          ),
          pre: ({ children }) => (
            <pre className="font-mono text-[13px] p-3 my-2 rounded-lg bg-cream/80 border border-soft overflow-x-auto">
              {children}
            </pre>
          ),
          a: ({ href, children }) => (
            <a
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              className="text-gold-dark underline underline-offset-2 hover:text-navy"
            >
              {children}
            </a>
          ),
          blockquote: ({ children }) => {
            const parsed = parseSourceBlock(children);
            if (parsed) {
              return (
                <div
                  className={`my-2 px-3 py-2 rounded-lg border ${parsed.tone} text-[12px] lg:text-[13px] not-italic flex items-start gap-2`}
                >
                  <span className="font-medium shrink-0 uppercase tracking-wider text-[10px] mt-0.5">
                    {parsed.label === 'source' ? '🔗 来源' : '📄 引用'}
                  </span>
                  <div className="flex-1 min-w-0">{parsed.rest}</div>
                </div>
              );
            }
            return (
              <blockquote className="border-l-2 border-gold-soft pl-3 my-2 text-dusk italic">
                {children}
              </blockquote>
            );
          },
          hr: () => <hr className="my-3 border-soft" />,
        }}
      >
        {safe}
      </ReactMarkdown>
    </div>
  );
}
